/* @flow */
import { createDuck } from 'redux-duck'
import { createAction } from 'redux-actions'
import { createActionMeta, standardSet } from '../../util/redux'
import omit from 'lodash.omit'

// Types
import type { Alert } from '../zone/types'
import type { ActivityFeedEntry } from '../event'
import type { Feature } from '@rachio-npm/com-rachio-web-core/types/feature'
import type {
  Device,
  Gen1IrrigationController,
  Gen2IrrigationController,
  LinkedSensor,
  WiringPosition
} from './types'

export * from './methods'
export * from './crud'
export * from './selectors'

/**
 * Exports device redux module in `Ducks` format
 * @see {@link https://github.com/erikras/ducks-modular-redux}
 * @module ducks/device
 */

// NAMESPACE //////////////////////////////////////////////////////////////////

const device = createDuck('DEVICE', 'RACHIO')

// ACTION TYPES ///////////////////////////////////////////////////////////////
const TRANSFER_OWNERSHIP_INIT = device.defineType('TRANSFER_OWNERSHIP_INIT')
export const TRANSFER_OWNERSHIP_COMPLETE = device.defineType(
  'TRANSFER_OWNERSHIP_COMPLETE'
)
const ADD_SCHEDULE_IDS = device.defineType('ADD_SCHEDULE_IDS')
const GET_BY_SERIAL_NUMBER_INIT = device.defineType('GET_BY_SERIAL_NUMBER_INIT')
const GET_BY_SERIAL_NUMBER_COMPLETE = device.defineType(
  'GET_BY_SERIAL_NUMBER_COMPLETE'
)
const GET_STATE_INIT = device.defineType('GET_STATE_INIT')
const GET_STATE_COMPLETE = device.defineType('GET_STATE_COMPLETE')
const GET_ZONES_INIT = device.defineType('GET_ZONES_INIT')
const GET_ZONES_COMPLETE = device.defineType('GET_ZONES_COMPLETE')
const GET_SCHEDULES_INIT = device.defineType('GET_SCHEDULES_INIT')
const GET_SCHEDULES_COMPLETE = device.defineType('GET_SCHEDULES_COMPLETE')
const GET_DEVICE_INIT = device.defineType('GET_DEVICE_INIT')
const GET_DEVICE_COMPLETE = device.defineType('GET_DEVICE_COMPLETE')
const GET_DEVICE_DETAILS_COMPLETE = device.defineType(
  'GET_DEVICE_DETAILS_COMPLETE'
)
const ZONE_RUN_EVENT = device.defineType('ZONE_RUN_EVENT')
const UPDATE_INIT = device.defineType('UPDATE_INIT')
const UPDATE_COMPLETE = device.defineType('UPDATE_COMPLETE')
const DELETE_INIT = device.defineType('DELETE_INIT')
const DELETE_COMPLETE = device.defineType('DELETE_COMPLETE')
const SET = device.defineType('SET')
const UPDATE_META = device.defineType('UPDATE_META')
const REMOVE = device.defineType('REMOVE')
const GET_DETAILS_INIT = device.defineType('GET_DETAILS_INIT')
const GET_DETAILS_COMPLETE = device.defineType('GET_DETAILS_COMPLETE')
const SET_DETAILS = device.defineType('SET_DETAILS')
const SET_RAIN_DELAY_INIT = device.defineType('SET_RAIN_DELAY_INIT')
const SET_RAIN_DELAY_COMPLETE = device.defineType('SET_RAIN_DELAY_COMPLETE')
const UPDATE_DETAILS_INIT = device.defineType('UPDATE_DETAILS_INIT')
const UPDATE_DETAILS_COMPLETE = device.defineType('UPDATE_DETAILS_COMPLETE')
const GET_FEATURES_COMPLETE = device.defineType('GET_FEATURES_COMPLETE')
const CREATE_RAIN_SENSOR_INIT = device.defineType('CREATE_RAIN_SENSOR_INIT')
const CREATE_RAIN_SENSOR_COMPLETE = device.defineType(
  'CREATE_RAIN_SENSOR_COMPLETE'
)
const REMOVE_RAIN_SENSOR_INIT = device.defineType('REMOVE_RAIN_SENSOR_INIT')
const REMOVE_RAIN_SENSOR_COMPLETE = device.defineType(
  'REMOVE_RAIN_SENSOR_COMPLETE'
)
const REMOVE_WIRELESS_SENSOR_INIT = device.defineType(
  'REMOVE_WIRELESS_SENSOR_INIT'
)
const REMOVE_WIRELESS_SENSOR_COMPLETE = device.defineType(
  'REMOVE_WIRELESS_SENSOR_COMPLETE'
)
const GET_UPCOMING_EVENTS_INIT = device.defineType('GET_UPCOMING_EVENTS_INIT')
const GET_UPCOMING_EVENTS_COMPLETE = device.defineType(
  'GET_UPCOMING_EVENTS_COMPLETE'
)
const GET_NEXT_EVENT_INIT = device.defineType('GET_NEXT_EVENT_INIT')
const GET_NEXT_EVENT_COMPLETE = device.defineType('GET_NEXT_EVENT_COMPLETE')
const GET_ACTIVE_ALERTS_INIT = device.defineType('GET_ACTIVE_ALERTS_INIT')
const GET_ACTIVE_ALERTS_COMPLETE = device.defineType(
  'GET_ACTIVE_ALERTS_COMPLETE'
)
const DISMISS_ALERTS_INIT = device.defineType('DISMISS_ALERTS_INIT')
const DISMISS_ALERTS_COMPLETE = device.defineType('DISMISS_ALERTS_COMPLETE')
const SET_SCHEDULE_IDS = device.defineType('SET_SCHEDULE_IDS')
const SET_ZONE_IDS = device.defineType('SET_ZONE_IDS')
const GET_WATERING_AGGREGATES_INIT = device.defineType(
  'GET_WATERING_AGGREGATES_INIT'
)
const GET_WATERING_AGGREGATES_COMPLETE = device.defineType(
  'GET_WATERING_AGGREGATES_COMPLETE'
)

const GET_WATERING_SUMMARY_INIT = device.defineType('GET_WATERING_SUMMARY_INIT')
const GET_WATERING_SUMMARY_COMPLETE = device.defineType(
  'GET_WATERING_SUMMARY_COMPLETE'
)
const GET_WATERING_SUMMARY_COMPLETE_FOR_CSV = device.defineType(
  'GET_WATERING_SUMMARY_COMPLETE_FOR_CSV'
)
const GET_NETWORK_STATE_INIT = device.defineType('GET_NETWORK_STATE_INIT')
const GET_NETWORK_STATE_COMPLETE = device.defineType(
  'GET_NETWORK_STATE_COMPLETE'
)

// Util
const CLEAR_SOM_BASED_DATA = device.defineType('CLEAR_SOM_BASED_DATA')

// ACTION CREATORS ////////////////////////////////////////////////////////////

// Note that we're not using `createDuck`'s `createAction` function as it
// doesn't automatically handle errors
export const clearSOMData = createAction(
  CLEAR_SOM_BASED_DATA,
  null,
  createActionMeta
)
export const getBySerialNumberInit = createAction(GET_BY_SERIAL_NUMBER_INIT)
export const getBySerialNumberComplete = createAction(
  GET_BY_SERIAL_NUMBER_COMPLETE
)
export const getStateInit = createAction(GET_STATE_INIT)
export const getStateComplete = createAction(
  GET_STATE_COMPLETE,
  null,
  createActionMeta
)
export const getZonesInit = createAction(GET_ZONES_INIT)
export const getZonesComplete = createAction(GET_ZONES_COMPLETE)
export const getSchedulesInit = createAction(GET_SCHEDULES_INIT)
export const getSchedulesComplete = createAction(GET_SCHEDULES_COMPLETE)
export const getDeviceInit = createAction(GET_DEVICE_INIT)
export const updateMeta = createAction(UPDATE_META)
export const transferOwnershipInit = createAction(TRANSFER_OWNERSHIP_INIT)
export const transferOwnershipComplete = createAction(
  TRANSFER_OWNERSHIP_COMPLETE
)
export const getDeviceComplete = createAction(GET_DEVICE_COMPLETE)
export const getDeviceDetailsComplete = createAction(
  GET_DEVICE_DETAILS_COMPLETE
)
export const dismissAlertsInit = createAction(DISMISS_ALERTS_INIT)
export const dismissAlertsComplete = createAction(DISMISS_ALERTS_COMPLETE)
export const getWateringAggregatesInit = createAction(
  GET_WATERING_AGGREGATES_INIT,
  null,
  createActionMeta
)
export const getWateringAggregatesComplete = createAction(
  GET_WATERING_AGGREGATES_COMPLETE,
  null,
  createActionMeta
)
export const getWateringSummaryInit = createAction(GET_WATERING_SUMMARY_INIT)
export const getWateringSummaryComplete = createAction(
  GET_WATERING_SUMMARY_COMPLETE
)
export const getWateringSummaryCompleteForCSV = createAction(
  GET_WATERING_SUMMARY_COMPLETE_FOR_CSV
)
export const zoneRunEvent = createAction(
  ZONE_RUN_EVENT,
  evt => evt.zoneRunStatus,
  evt => ({ ...evt, ...createActionMeta() })
)
export const getNetworkStateInit = createAction(GET_NETWORK_STATE_INIT)
export const getNetworkStateComplete = createAction(GET_NETWORK_STATE_COMPLETE)

// update
export const updateInit = createAction(UPDATE_INIT)

type UpdateCompleteAction = {|
  type: 'RACHIO/DEVICE/UPDATE_COMPLETE',
  payload: Device | Error,
  error?: boolean,
  meta: { actionCreatedAt: number }
|}
export const updateComplete: (
  Object | Error
) => UpdateCompleteAction = createAction(UPDATE_COMPLETE)

// set
type SetAction = {|
  type: 'RACHIO/DEVICE/SET',
  payload: Device,
  meta: { actionCreatedAt: number },
  error?: boolean
|}
export const set: Device => SetAction = createAction(SET)

// remove
export const deleteDeviceInit = createAction(DELETE_INIT)
export const deleteDeviceComplete = createAction(DELETE_COMPLETE)

type RemoveAction = {|
  type: 'RACHIO/DEVICE/REMOVE',
  payload: string,
  meta: { actionCreatedAt: number },
  error?: boolean
|}
export const remove: string => RemoveAction = createAction(REMOVE)

// details
type GetDetailsInitAction = {|
  type: 'RACHIO/DEVICE/GET_DETAILS_INIT',
  payload: string,
  error?: false,
  meta?: Object
|}
export const getDetailsInit: (
  deviceId: string
) => GetDetailsInitAction = createAction(GET_DETAILS_INIT)

type GetDetailsCompleteAction = {|
  type: 'RACHIO/DEVICE/GET_DETAILS_COMPLETE',
  payload: (Gen1IrrigationController | Gen2IrrigationController) | Error,
  error: boolean,
  meta?: Object
|}
export const getDetailsComplete: (
  (Gen1IrrigationController | Gen2IrrigationController) | Error
) => GetDetailsCompleteAction = createAction(GET_DETAILS_COMPLETE)

type SetDetailsAction = {
  type: 'RACHIO/DEVICE/SET_DETAILS',
  payload: Gen1IrrigationController | Gen2IrrigationController,
  meta: { actionCreatedAt: number }
}
export const setDetails: (
  Gen1IrrigationController | Gen2IrrigationController
) => SetDetailsAction = createAction(SET_DETAILS)

type UpdateDetailsInitAction = {|
  type: 'RACHIO/DEVICE/UPDATE_DETAILS_INIT',
  payload: Object,
  error?: false,
  meta?: Object
|}
export const updateDetailsInit: (
  updateData: Object
) => UpdateDetailsInitAction = createAction(UPDATE_DETAILS_INIT)

type UpdateDetailsCompleteAction = {|
  type: 'RACHIO/DEVICE/UPDATE_DETAILS_COMPLETE',
  payload: (Gen1IrrigationController | Gen2IrrigationController) | Error,
  error: boolean,
  meta?: Object
|}
export const updateDetailsComplete: (
  (Gen1IrrigationController | Gen2IrrigationController) | Error
) => UpdateDetailsCompleteAction = createAction(UPDATE_DETAILS_COMPLETE)

// feature flags
type GetFeaturesCompleteAction = {|
  type: 'RACHIO/DEVICE/GET_FEATURES_COMPLETE',
  payload: { deviceId: string, features: Array<Feature> } | Error,
  error?: boolean
|}
export const getFeaturesComplete: Object => GetFeaturesCompleteAction = createAction(
  GET_FEATURES_COMPLETE
)

// create rain sensor
type RainSensorCreateData = {|
  linked_irrigation_controller_device_id: string,
  wiring_position: WiringPosition
|}
type CreateRainSensorInitAction = {|
  type: 'RACHIO/DEVICE/CREATE_RAIN_SENSOR_INIT',
  payload: RainSensorCreateData,
  error?: false,
  meta?: null
|}
export const createRainSensorInit: (
  createData: RainSensorCreateData
) => CreateRainSensorInitAction = createAction(CREATE_RAIN_SENSOR_INIT)

type CreateRainSensorCompleteAction = {|
  type: 'RACHIO/DEVICE/CREATE_RAIN_SENSOR_COMPLETE',
  payload:
    | {|
        value: LinkedSensor
      |}
    | Error,
  error?: boolean,
  meta?: null
|}
export const createRainSensorComplete: (
  RespOrError: Object
) => CreateRainSensorCompleteAction = createAction(CREATE_RAIN_SENSOR_COMPLETE)

// remove rain sensor
type RemoveRainSensorInitAction = {|
  type: 'RACHIO/DEVICE/REMOVE_RAIN_SENSOR_INIT',
  payload: string,
  error?: false
|}
export const removeRainSensorInit: (
  idToRemove: string
) => RemoveRainSensorInitAction = createAction(REMOVE_RAIN_SENSOR_INIT)

type RemoveRainSensorCompleteAction = {|
  type: 'RACHIO/DEVICE/REMOVE_RAIN_SENSOR_COMPLETE',
  payload: Object | Error,
  error?: boolean
|}
export const removeRainSensorComplete: Object => RemoveRainSensorCompleteAction = createAction(
  REMOVE_RAIN_SENSOR_COMPLETE
)

// remove wireless sensor
type RemoveWirelessSensorInitAction = {|
  type: 'RACHIO/DEVICE/REMOVE_WIRELESS_SENSOR_INIT',
  payload: string,
  error?: false
|}
export const removeWirelessSensorInit: (
  idToRemove: string
) => RemoveWirelessSensorInitAction = createAction(REMOVE_WIRELESS_SENSOR_INIT)

type RemoveWirelessSensorCompleteAction = {|
  type: 'RACHIO/DEVICE/REMOVE_WIRELESS_SENSOR_COMPLETE',
  payload: Object | Error,
  error?: boolean
|}
export const removeWirelessSensorComplete: Object => RemoveWirelessSensorCompleteAction = createAction(
  REMOVE_WIRELESS_SENSOR_COMPLETE
)

// get upcoming events
type GetUpcomingEventsInitAction = {|
  type: 'RACHIO/DEVICE/GET_UPCOMING_EVENTS_INIT',
  payload: { device_id: string, hours_ahead: number },
  error?: false
|}
export const getUpcomingEventsInit: ({
  device_id: string,
  hours_ahead: number
}) => GetUpcomingEventsInitAction = createAction(GET_UPCOMING_EVENTS_INIT)

export type GetUpcomingEventsCompleteAction = {|
  type: 'RACHIO/DEVICE/GET_UPCOMING_EVENTS_COMPLETE',
  payload: { entries: Array<ActivityFeedEntry> } | Error,
  error?: boolean
|}
export const getUpcomingEventsComplete: Object => GetUpcomingEventsCompleteAction = createAction(
  GET_UPCOMING_EVENTS_COMPLETE
)

// get next event
type GetNextEventInitAction = {|
  type: 'RACHIO/DEVICE/GET_NEXT_EVENTS_INIT',
  payload: { device_id: string },
  error?: false
|}

export const getNextEventInit: ({
  device_id: string
}) => GetNextEventInitAction = createAction(GET_NEXT_EVENT_INIT)

type GetNextEventCompleteAction = {|
  type: 'RACHIO/DEVICE/GET_NEXT_EVENT_COMPLETE',
  payload: ActivityFeedEntry | Error,
  error?: boolean
|}
export const getNextEventComplete: (
  ActivityFeedEntry | Error
) => GetNextEventCompleteAction = createAction(GET_NEXT_EVENT_COMPLETE)

// get active alerts
type GetActiveAlertsInitAction = {|
  type: 'RACHIO/DEVICE/GET_ACTIVE_ALERTS_INIT',
  payload: { device_id: string },
  error?: false
|}
export const getActiveAlertsInit: ({
  device_id: string
}) => GetActiveAlertsInitAction = createAction(GET_ACTIVE_ALERTS_INIT)

type GetActiveAlertsCompleteAction = {|
  type: 'RACHIO/DEVICE/GET_ACTIVE_ALERTS_COMPLETE',
  payload: { alerts: Array<Alert> } | Error,
  error?: boolean
|}
export const getActiveAlertsComplete: Object => GetActiveAlertsCompleteAction = createAction(
  GET_ACTIVE_ALERTS_COMPLETE
)

// set schedule ids
type SetScheduleIdsAction = {
  type: 'RACHIO/DEVICE/SET_SCHEDULE_IDS',
  payload: { deviceId: string, scheduleIds: Array<string> },
  error?: false
}
export const setScheduleIds: ({
  deviceId: string,
  scheduleIds: Array<string>
}) => SetScheduleIdsAction = createAction(SET_SCHEDULE_IDS)

// add schedule ids
type AddScheduleIdsAction = {|
  type: 'RACHIO/DEVICE/ADD_SCHEDULE_IDS',
  payload: { deviceId: string, scheduleIds: Array<string> },
  error?: false
|}

export const addScheduleIds: ({
  deviceId: string,
  scheduleIds: Array<string>
}) => AddScheduleIdsAction = createAction(ADD_SCHEDULE_IDS)

// set zone ids
type SetZoneIdsAction = {
  type: 'RACHIO/DEVICE/SET_ZONE_IDS',
  payload: { deviceId: string, zoneIds: Array<string> },
  error?: false
}
export const setZoneIds: ({
  deviceId: string,
  zoneIds: Array<string>
}) => SetZoneIdsAction = createAction(SET_ZONE_IDS)

export const setRainDelayInit = createAction(SET_RAIN_DELAY_INIT)
export const setRainDelayComplete = createAction(SET_RAIN_DELAY_COMPLETE)

// REDUCER ////////////////////////////////////////////////////////////////////

const initialState = {
  META: {
    addStatus: {} // ['SEARCHING', deviceProto, errorAction]
  }
}

export default device.createReducer(
  {
    // Some data we get from the backend is based on a user's current SOM.
    // If the user changes their SOM we need to clear this data
    [CLEAR_SOM_BASED_DATA]: state => {
      const clonedState = Object.assign({}, state)
      const deviceIds: Array<string> = Object.keys(clonedState).filter(
        key => key !== 'META'
      )

      deviceIds.forEach(deviceId => {
        const curDevice = clonedState[deviceId]

        if (curDevice.META) {
          // Delete SOM based data
          delete curDevice.META.alerts
          delete curDevice.META.wateringSummary
        }
      })

      return clonedState
    },
    [DISMISS_ALERTS_COMPLETE]: (state, action) => {
      if (action.error) return state

      // Grab the alerts on this device
      const alerts =
        state[action.payload.deviceId] &&
        state[action.payload.deviceId].META &&
        state[action.payload.deviceId].META.alerts

      if (alerts) {
        // Make a shallow copy, so we remove items on the copy not the original
        const alertsCopy = alerts.slice()

        // Find the index for the alertIds we want to remove, then remove them
        for (let i = 0; i < action.payload.alertIds.length; i++) {
          const idx = alertsCopy.findIndex(
            alert => alert.id === action.payload.alertIds[i]
          )
          alertsCopy.splice(idx, 1)
        }

        // Return {...state, alerts: alertsCopy}
        return {
          ...state,
          [action.payload.deviceId]: {
            ...state[action.payload.deviceId],
            META: {
              ...(state[action.payload.deviceId] || {}).META,
              alerts: alertsCopy
            }
          }
        }
      } else {
        return state
      }
    },
    [GET_ACTIVE_ALERTS_COMPLETE]: (state, action) => {
      if (action.error) {
        return state
      }

      return {
        ...state,
        [action.payload.deviceId]: {
          ...state[action.payload.deviceId],
          META: {
            ...(state[action.payload.deviceId] || {}).META,
            alerts: action.payload.alerts
          }
        }
      }
    },
    [GET_WATERING_SUMMARY_COMPLETE]: (state, action) => {
      if (action.error) {
        return state
      }

      const device = state[action.payload.deviceId]
      const META = (device && device.META) || {}

      return {
        ...state,
        [action.payload.deviceId]: {
          ...device,
          META: {
            ...META,
            wateringSummary: {
              ...(META.wateringSummary || {}),
              [action.payload.offset]: action.payload.wateringSummary
            }
          }
        }
      }
    },
    [GET_BY_SERIAL_NUMBER_INIT]: state => ({
      ...state,
      META: { ...state.META, addStatus: 'SEARCHING' }
    }),
    [GET_BY_SERIAL_NUMBER_COMPLETE]: (state, action) => ({
      ...state,
      META: { ...state.META, addStatus: action }
    }),
    [GET_DEVICE_INIT]: (state, action) => ({
      ...state,
      [action.payload.deviceId]: { META: { fetching: true } }
    }),
    [GET_DEVICE_COMPLETE]: (state, action) => {
      if (action.error) {
        return {
          ...state,
          [action.payload.deviceId]: { META: { fetching: action } }
        }
      } else {
        return {
          ...state,
          [action.payload.device.id]: {
            META: {
              ...(state[action.payload.device.id] || {}).META,
              fetching: false
            },
            ...action.payload.device
          }
        }
      }
    },
    [SET]: standardSet,
    [UPDATE_META]: (state, action) => {
      const { payload: { deviceId, data } = {} } = action
      const { [deviceId]: device = {} } = state

      return {
        ...state,
        [deviceId]: {
          ...device,
          META: {
            ...device.META,
            ...data
          }
        }
      }
    },
    [REMOVE]: (state, action) => omit(state, action.payload),
    [SET_DETAILS]: (state, action) => ({
      ...state,
      [action.payload.id]: {
        ...state[action.payload.id],
        META: {
          ...(state[action.payload.id] && state[action.payload.id].META
            ? state[action.payload.id].META
            : {}),
          details: {
            // If `correctSchedule` is `false`, state is not valid. We will
            // eventually get a new event when `correctSchedule` becomes `true`,
            // which will request the state again and give us the correct state
            ...(action.payload.correctSchedule === false
              ? omit(action.payload, 'state')
              : action.payload)
          }
        }
      }
    }),
    [GET_STATE_INIT]: (state, action) => ({
      ...state,
      [action.payload.deviceId]: {
        ...state[action.payload.deviceId],
        META: {
          ...(state[action.payload.deviceId] || {}).META,
          fetchingState: true
        }
      }
    }),
    [GET_STATE_COMPLETE]: (state, action) => {
      if (action.error) {
        return {
          ...state,
          [action.payload.deviceId]: {
            ...state[action.payload.deviceId],
            META: {
              ...(state[action.payload.deviceId] || {}).META,
              fetchingState: action
            }
          }
        }
      } else {
        return {
          ...state,
          [action.payload.deviceId]: {
            ...state[action.payload.deviceId],
            META: {
              ...(state[action.payload.deviceId] || {}).META,
              fetchingState: false,
              state: action.payload,
              stateUpdatedAt: action.meta.actionCreatedAt
            }
          }
        }
      }
    },
    [GET_FEATURES_COMPLETE]: (state, action) => {
      if (action.error) {
        return state
      } else {
        return {
          ...state,
          [action.payload.deviceId]: {
            ...state[action.payload.deviceId],
            META: {
              ...(state[action.payload.deviceId] || {}).META,
              features: action.payload.features
            }
          }
        }
      }
    },
    [GET_ZONES_COMPLETE]: (state, action) => {
      if (action.error) {
        return {
          ...state,
          [action.payload.deviceId]: {
            META: {
              ...(state[action.payload.deviceId] || {}).META,
              fetchingZones: action
            }
          }
        }
      } else {
        return {
          ...state
        }
      }
    },
    [ZONE_RUN_EVENT]: (state, action) => ({
      ...state,
      [action.meta.deviceId]: {
        ...state[action.meta.deviceId],
        META: {
          ...state[action.meta.deviceId].META,
          zoneRunStatus: action.payload,
          zoneRunStatusUpdatedAt: action.meta.actionCreatedAt
        }
      }
    }),
    [SET_SCHEDULE_IDS]: (state, action) => ({
      ...state,
      [action.payload.deviceId]: {
        ...state[action.payload.deviceId],
        META: {
          ...(state[action.payload.deviceId] || {}).META,
          scheduleIds: action.payload.scheduleIds
        }
      }
    }),
    [ADD_SCHEDULE_IDS]: (state, action) => {
      const device = state[action.payload.deviceId]
      const META = device && device.META
      return {
        ...state,
        [action.payload.deviceId]: {
          ...device,
          META: {
            ...META,
            scheduleIds: [
              ...action.payload.scheduleIds,
              ...((META && META.scheduleIds) || [])
            ]
          }
        }
      }
    },
    [SET_ZONE_IDS]: (state, action) => ({
      ...state,
      [action.payload.deviceId]: {
        ...state[action.payload.deviceId],
        META: {
          ...(state[action.payload.deviceId] || {}).META,
          zoneIds: action.payload.zoneIds
        }
      }
    })
  },
  initialState
)
